import { UIView } from "uicore-ts"
import { RHAtomicFocusItem } from "./RHAtomicFocusItem"
import { RHFocusManager } from "./RHFocusManager"


declare module "uicore-ts" {
    
    interface UIView {
        /**
         * The focus manager responsible for Tab navigation within this view.
         *
         * By convention every `UIView` subclass that owns focusable controls
         * declares this field. When this view is included in a parent's
         * `focusableItems` list, `RHFocusManager._expandItems` will
         * automatically replace it with the expanded contents of this manager,
         * so the view controls its own internal Tab order without the parent
         * needing to know about its structure.
         *
         * To suppress expansion and treat the view as a single Tab stop,
         * include `view.atomicFocusItem()` in the list instead.
         *
         * See `RHFocusManager` and `RHPageFocusManager` for the full usage pattern.
         */
        _focusManager?: RHFocusManager
        
        /**
         * Returns an `RHAtomicFocusItem` wrapping this view.
         *
         * Include this in a `focusableItems` list instead of the view itself
         * to prevent auto-expansion of the view's `_focusManager`. Tab will
         * land on this view as a single stop rather than cycling through its
         * internal focusable controls.
         *
         * ### Example
         * ```typescript
         * // Auto-expanded — addressCard manages its own internal Tab order
         * items.push(this.addressCard)
         *
         * // Atomic — Tab lands on the card as a single stop
         * items.push(this.addressCard.atomicFocusItem())
         * ```
         */
        atomicFocusItem(): RHAtomicFocusItem
    }
    
    interface UIViewController {
        /**
         * The focus manager responsible for Tab navigation within this view controller.
         *
         * Register it with `RHPageFocusManager.shared` in `viewWillAppear` and
         * deregister in `viewWillDisappear`. See `RHFocusManager` and
         * `RHPageFocusManager` for the full usage pattern.
         */
        _focusManager?: RHFocusManager
    }
    
}


UIView.prototype.atomicFocusItem = function(this: UIView): RHAtomicFocusItem {
    return new RHAtomicFocusItem(this)
}
